import { useAppSelector } from '@/redux/hooks.ts'
import { selectThemeStyle } from '@/features/setting/setting.slice.ts'
import { IdeyaButton } from '@/common/antd/ideya-button/ideya-button.tsx'
import { convertPxToNumber } from '@/utils/helpers.ts'
import {
  AccountFirmRole,
  AccountGroupRole,
} from '@/enums/management-input.enum.ts'
import { VerticalSeparator } from '@/common/components/vertical-separator.tsx'
import { useIsMobile } from '@/common/hooks/use-is-mobile.ts'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import { IdeyaSegmented } from '@/common/antd/ideya-segmented.tsx'
import Select from 'antd/es/select/index'
import Input from 'antd/es/input'
import Spin from 'antd/es/spin/index'
import Tooltip from 'antd/es/tooltip/index'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useListFirmOptionsQuery } from '@/apis/option/option-api.api.ts'
import { getCategoryOptions } from '@/utils/selector-options.ts'
import { useTranslation } from 'react-i18next'
import { useIsSuperViewer } from '@/common/hooks/use-is-superviewer.ts'
import { MailListType } from '@/enums/mail-list.enum.ts'

// Local filter state kept in the page (not the shared managementInput slice),
// so the mail-list filters never pollute the account-management filters.
export interface MailListFilters {
  firmId: string
  firmRole: string
  category: string
  groupRole: string
  searchName: string
}

interface MailListControlsProps {
  listType: MailListType
  onListTypeChange: (value: MailListType) => void
  filters: MailListFilters
  onFilterChange: (patch: Partial<MailListFilters>) => void
  onCreate: () => void
}

export const MailListControls: React.FC<MailListControlsProps> = ({
  listType,
  onListTypeChange,
  filters,
  onFilterChange,
  onCreate,
}) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const isMobile = useIsMobile(768)
  const isSuperViewer = useIsSuperViewer()
  const { currentData: firmOptionData, isLoading: isLoadingFirmOptions } =
    useListFirmOptionsQuery()

  const segmentedOptions = [
    {
      value: MailListType.Block,
      label: t('mail_list.controls.block_list'),
    },
    {
      value: MailListType.White,
      label: t('mail_list.controls.white_list'),
    },
  ]

  const listTypeSegmented = (
    <Row wrap={false} align={'middle'}>
      <IdeyaSegmented
        value={listType}
        onChange={(value) => onListTypeChange(value as MailListType)}
        options={segmentedOptions}
      />
      <Tooltip
        title={
          <>
            <div>{t('mail_list.modal.block_list_tooltip')}</div>
            <div>{t('mail_list.modal.white_list_tooltip')}</div>
          </>
        }
      >
        <InfoCircleOutlined
          style={{
            color: '#D3A342',
            marginLeft: 8,
            cursor: 'pointer',
          }}
        />
      </Tooltip>
    </Row>
  )

  const firmSelect = (
    <>
      {isLoadingFirmOptions ? (
        <Spin />
      ) : (
        <Select
          showSearch
          value={filters.firmId}
          style={{ width: isMobile ? '100%' : '180px' }}
          onChange={(value) => onFilterChange({ firmId: value })}
          options={[
            {
              value: '',
              label: t('common.user_controls.all_firms'),
            },
            ...(firmOptionData?.data.map((firm) => ({
              value: String(firm.value),
              label: firm.label,
            })) || []),
          ]}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
        />
      )}
    </>
  )

  const firmRoleSelect = (
    <Select
      showSearch
      value={filters.firmRole}
      style={{ width: isMobile ? '100%' : '150px' }}
      onChange={(value) => onFilterChange({ firmRole: value })}
      options={[
        {
          value: AccountFirmRole.All,
          label: t('common.user_controls.all_firm_roles'),
        },
        { value: AccountFirmRole.Compliance, label: 'Compliance' },
        { value: AccountFirmRole.Normal, label: 'Normal' },
      ]}
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
    />
  )

  const categorySelect = (
    <Select
      showSearch
      value={filters.category}
      style={{ width: isMobile ? '100%' : '200px' }}
      onChange={(value) => onFilterChange({ category: value })}
      options={getCategoryOptions(t)}
    />
  )

  const groupRoleSelect = (
    <Select
      showSearch
      value={filters.groupRole}
      style={{ width: isMobile ? '100%' : '150px' }}
      onChange={(value) => onFilterChange({ groupRole: value })}
      options={[
        {
          value: AccountGroupRole.All,
          label: t('common.user_controls.all_group_roles'),
        },
        { value: AccountGroupRole.Captain, label: 'Captain' },
        { value: AccountGroupRole.Manager, label: 'Manager' },
        { value: AccountGroupRole.Contributor, label: 'Contributor' },
      ]}
    />
  )

  const searchInput = (
    <Input
      placeholder={t('common.user_controls.search_name')}
      value={filters.searchName}
      onChange={(e) => onFilterChange({ searchName: e.target.value })}
      allowClear
    />
  )

  const addButton = !isSuperViewer && (
    <IdeyaButton onClick={onCreate} style={{ width: 120 }}>
      {t('mail_list.controls.add_mail')}
    </IdeyaButton>
  )

  return (
    <Row
      gutter={[0, convertPxToNumber(themeStyle.marginXS)]}
      align={'bottom'}
      justify={isMobile ? 'start' : 'space-between'}
      style={{
        marginBottom: themeStyle.marginSM,
        flexDirection: isMobile ? 'column' : 'row',
      }}
    >
      <Col flex={isMobile ? 'unset' : 1}>
        {isMobile ? (
          <Row gutter={[12, 12]}>
            <Col xs={24}>{listTypeSegmented}</Col>
            <Col xs={24}>
              <Row>{firmSelect}</Row>
            </Col>
            <Col xs={24}>
              <Row>{firmRoleSelect}</Row>
            </Col>
            <Col xs={24}>
              <Row>{categorySelect}</Row>
            </Col>
            <Col xs={24}>
              <Row>{groupRoleSelect}</Row>
            </Col>
            <Col xs={24}>
              <Row>{searchInput}</Row>
            </Col>
            {addButton && (
              <Col
                xs={24}
                style={{ display: 'flex', justifyContent: 'flex-end' }}
              >
                {addButton}
              </Col>
            )}
          </Row>
        ) : (
          <Row
            gutter={[
              convertPxToNumber(themeStyle.marginBase),
              convertPxToNumber(themeStyle.marginBase),
            ]}
            align={'middle'}
            style={{ flexWrap: 'wrap' }}
          >
            <Col>{listTypeSegmented}</Col>
            <Col>
              <VerticalSeparator />
            </Col>
            <Col>
              <Row>{firmSelect}</Row>
            </Col>
            <Col>
              <Row>{firmRoleSelect}</Row>
            </Col>
            <Col>
              <VerticalSeparator />
            </Col>
            <Col>
              <Row>{categorySelect}</Row>
            </Col>
            <Col>
              <Row>{groupRoleSelect}</Row>
            </Col>
            <Col>
              <VerticalSeparator />
            </Col>
            <Col>
              <Row>{searchInput}</Row>
            </Col>
          </Row>
        )}
      </Col>
      {!isMobile && addButton && (
        <Row>
          <Col>{addButton}</Col>
        </Row>
      )}
    </Row>
  )
}
